import {
  ADDITIONAL_FACILITY_INDEXES,
  AHB_MESSAGE_TYPE,
  AHB_NAMESPACES_BY_VERSION,
  AHB_PROCEDURE_ID,
  AHB_PROCESSING_CODE,
  AHB_REHAB_TYPE,
  AHB_SUBPROCEDURE,
  AT_HOME_SITUATION,
  GENDER,
  HOME_CARE_SITUATION,
  MUSCOSKELETAL_RESILIENCE,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_FOCUS,
  SCHEMA_VERSION,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_CODE,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import { onPremHttpRequest } from "core/model/careseekers";
import { bytesToBase64 } from "core/model/crypto";
import {
  AHB_SUBMISSION_TIMEZONE,
  formatAhbXmlDate,
} from "core/model/utils/dates";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import { safeParseInt } from "core/model/utils/strings";
import { decryptFromSession } from "core/seald";
import {
  type SchemaVersion,
  AtHomeSituation,
  FileState,
  Gender,
  GKVGeneralFormData,
  GKVMedicalFormData,
  HomeCareSituation,
  PredicamentState,
  PredicamentYesNo,
  SingerAssessment,
  TypeOfRehab,
  ValueOf,
} from "core/types";
import { format, utcToZonedTime } from "date-fns-tz";
import { isFileHISStateful } from "core/model/files/utils";
import { v4 as uuidv4 } from "uuid";
import { nullsToUndefined } from "./shared";

type ProcessedFile = {
  "gdb:Dateityp": string;
  "gdb:Datei": string;
};

const SUPPORTED_FILE_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "jpg",
  "jpeg",
  "png",
  "tiff",
  "xls",
  "xlsx",
] as const;

function mapGender(
  value: Gender | null | undefined,
): "m" | "w" | "d" | "x" | undefined {
  switch (value) {
    case GENDER.MALE:
      return "m";
    case GENDER.FEMALE:
      return "w";
    case GENDER.DIVERSE:
      return "d";
    case GENDER.UNSPECIFIED:
      return "x";
    default:
      return undefined;
  }
}

function mapRecommendedRehabType(
  value: TypeOfRehab | undefined | null,
): 1 | 2 | 4 | undefined {
  switch (value) {
    case TYPE_OF_REHAB.AMBULANT:
      return AHB_REHAB_TYPE.AMBULANT;
    case TYPE_OF_REHAB.AMBULANT_MOBIL:
      return AHB_REHAB_TYPE.AMBULANT_MOBIL;
    case TYPE_OF_REHAB.STATIONARY:
      return AHB_REHAB_TYPE.STATIONARY;
    default:
      return undefined;
  }
}

export const beeintraechtigungMap: Record<
  keyof NonNullable<Omit<SingerAssessment["form"], "__typename">>,
  string
> = {
  food: "01",
  dressing_and_undressing: "02",
  hygiene: "03",
  shower: "04",
  stool: "05",
  urination: "06",
  toilet_use: "07",
  transfer: "08",
  wheelchair: "09",
  walking: "10",
  stairs: "11",
  listening: "12",
  talking: "13",
  reading: "14",
  writing: "15",
  orientation: "16",
  concentration: "17",
  planning: "18",
  social_interaction: "19",
  domestic_life_household: "20",
};

export function sumSingerAssessmentScores(
  assessment: GKVMedicalFormData | undefined,
): number {
  return Object.keys(beeintraechtigungMap).reduce((sum, field) => {
    const key = `singer_assessment_${field}` as keyof GKVMedicalFormData;
    const value = assessment?.[key];
    return sum + (typeof value === "number" ? value : 0);
  }, 0);
}

const mapTransportationCode = (
  type: ValueOf<typeof TRANSPORTATION_TYPE> | undefined | null,
  taxiDetail: ValueOf<typeof TRANSPORTATION_SUBTYPE> | undefined | null,
  ambulanceDetail: ValueOf<typeof TRANSPORTATION_SUBTYPE> | undefined | null,
): ValueOf<typeof TRANSPORTATION_CODE> | undefined => {
  switch (type) {
    case TRANSPORTATION_TYPE.MOBILE_REHAB:
      return TRANSPORTATION_CODE.MOBILE_REHAB;
    case TRANSPORTATION_TYPE.PUBLIC:
      return TRANSPORTATION_CODE.PUBLIC;
    case TRANSPORTATION_TYPE.CAR:
      return TRANSPORTATION_CODE.CAR;
    case TRANSPORTATION_TYPE.TAXI:
      switch (taxiDetail) {
        case TRANSPORTATION_SUBTYPE.WHEELCHAIR:
          return TRANSPORTATION_CODE.TAXI_WHEELCHAIR;
        case TRANSPORTATION_SUBTYPE.STRETCHER:
          return TRANSPORTATION_CODE.TAXI_STRETCHER;
        case TRANSPORTATION_SUBTYPE.LYING_DOWN:
          return TRANSPORTATION_CODE.TAXI_LYING_DOWN;
        default:
          return TRANSPORTATION_CODE.TAXI;
      }
    case TRANSPORTATION_TYPE.AMBULANCE:
      switch (ambulanceDetail) {
        case TRANSPORTATION_SUBTYPE.WHEELCHAIR:
          return TRANSPORTATION_CODE.AMBULANCE_WHEELCHAIR;
        case TRANSPORTATION_SUBTYPE.STRETCHER:
          return TRANSPORTATION_CODE.AMBULANCE_STRETCHER;
        case TRANSPORTATION_SUBTYPE.LYING_DOWN:
          return TRANSPORTATION_CODE.AMBULANCE_LYING_DOWN;
        default:
          return undefined;
      }
    default:
      return undefined;
  }
};

function predicamentMappingYesNo(
  value: PredicamentYesNo | undefined | null,
  fieldLabel: string,
  shouldValidate?: boolean,
): "0" | "1" | undefined {
  if (value === PREDICAMENT_STATE_NO) return "0";
  if (value === PREDICAMENT_STATE_YES) return "1";
  if (shouldValidate) {
    throw new Error(`[mapping] Invalid or missing value for ${fieldLabel}`);
  }
  return undefined;
}

function predicamentMappingYesNoUnknown(
  value: PredicamentState | undefined | null,
  fieldLabel: string,
  shouldValidate?: boolean,
): "0" | "1" | "2" | undefined {
  if (value === PREDICAMENT_STATE_NO) return "0";
  if (value === PREDICAMENT_STATE_YES) return "1";
  if (value === PREDICAMENT_STATE_UNKNOWN) return "2";
  if (shouldValidate) {
    throw new Error(`[mapping] Invalid or missing value for ${fieldLabel}`);
  }
  return undefined;
}

const predicamentMappingYesUnknown = (value: unknown): 1 | undefined =>
  value === true || value === "1" || value === 1 ? 1 : undefined;

function mapLivingSituation(
  living_situation?: HomeCareSituation | null,
  living_situation_at_home?: AtHomeSituation | null,
  shouldValidate?: boolean,
): "1" | "2" | "3" | "4" | undefined {
  if (living_situation === HOME_CARE_SITUATION.HOME) {
    if (living_situation_at_home === AT_HOME_SITUATION.ALONE) return "1";
    if (living_situation_at_home === AT_HOME_SITUATION.RELATIVES) return "2";
    if (shouldValidate) {
      throw new Error(
        `[mapLivingSituation] 'living_situation_at_home' value ${living_situation}`,
      );
    }
    return undefined;
  }

  if (living_situation === HOME_CARE_SITUATION.ASSISTED_LIVING) return "3";
  if (living_situation === HOME_CARE_SITUATION.CARE_HOME) return "4";
  if (shouldValidate) {
    throw new Error(
      `[mapLivingSituation] Invalid 'living_situation_at_home' value ${living_situation_at_home}`,
    );
  }
  return undefined;
}

export const REHAB_FOCUS_VALUE_TO_CODE: Record<
  ValueOf<typeof REHAB_FOCUS>,
  string
> = {
  [REHAB_FOCUS.HEART]: "01",
  [REHAB_FOCUS.BLOOD_VESSELS]: "02",
  [REHAB_FOCUS.INFLAMMATORY_DISEASE]: "03",
  [REHAB_FOCUS.MUSCOLOSKELETAL_DISEASE]: "04",
  [REHAB_FOCUS.GASTROENTEROLOGICAL_DISEASE]: "05",
  [REHAB_FOCUS.ENDOCRINE_DISEASE]: "06",
  [REHAB_FOCUS.RESPIRATORY_DISEASE]: "07",
  [REHAB_FOCUS.NEPHROLOGICAL_DISEASE]: "08",
  [REHAB_FOCUS.NEUROLOGICAL_DISEASE]: "09",
  [REHAB_FOCUS.ONCOLOGICAL_DISEASE]: "10",
  [REHAB_FOCUS.GYNAECOLOGICAL_DISEASE]: "11",
  [REHAB_FOCUS.SKIN_DISEASE]: "12",
  [REHAB_FOCUS.PSYCHOSOMATIC_DISORDER]: "13",
  [REHAB_FOCUS.MENTAL_ILLNESS]: "14",
  [REHAB_FOCUS.ADDICTIVE_DISORDER]: "15",
  [REHAB_FOCUS.BLOOD_DISEASE]: "16",
  [REHAB_FOCUS.GERIATRICS]: "19",
  [REHAB_FOCUS.OTHER]: "21",
  [REHAB_FOCUS.UROLOGICAL_DISEASE]: "22",
};

function getFileType(file: FileState): string {
  if (isFileHISStateful(file)) {
    const fileName = file.document_file_name || "";
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (
      extension &&
      SUPPORTED_FILE_EXTENSIONS.includes(
        extension as (typeof SUPPORTED_FILE_EXTENSIONS)[number],
      )
    ) {
      return `.${extension}`;
    }
  } else {
    if (file.file_type) {
      if (file.file_type.includes("/")) {
        const subtype = file.file_type.split("/").pop()?.toLowerCase();
        if (subtype === "jpeg") return ".jpg";
        if (
          subtype &&
          SUPPORTED_FILE_EXTENSIONS.includes(
            subtype as (typeof SUPPORTED_FILE_EXTENSIONS)[number],
          )
        ) {
          return `.${subtype}`;
        }
        return ".pdf";
      }
      return file.file_type.startsWith(".")
        ? file.file_type
        : `.${file.file_type}`;
    }
  }

  return ".pdf";
}

async function getFileContentAsBase64(
  file: FileState,
  externalId: string,
  on_premise_authorization_token?: string,
  on_premise_domain_ssl?: string,
): Promise<string> {
  if (isFileHISStateful(file)) {
    if (!on_premise_authorization_token || !on_premise_domain_ssl) {
      throw new Error("Missing on-premise credentials for HIS file download");
    }

    const response = await onPremHttpRequest({
      method: "GET",
      on_premise_authorization_token,
      on_premise_domain_ssl,
      path: `patients/${externalId}/documents/${file.document_id}`,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch HIS file: ${response.status}`);
    }

    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    return bytesToBase64(uint8Array);
  } else {
    if (!file.link || !file.file_name?.seald_content || !file.file_type) {
      throw new Error("Filev2 file missing required properties for download");
    }

    const response = await fetch(file.link, { headers: { Accept: "*/*" } });
    const text = await response.text();
    if (!text) {
      throw new Error("Couldn't fetch encrypted file content");
    }

    const decryptedBase64File = await decryptFromSession({
      context: "file",
      encryptedMessage: text,
    });

    if (!decryptedBase64File) {
      throw new Error("Couldn't decrypt file content");
    }

    return decryptedBase64File;
  }
}

async function processAndValidateFiles(
  files: FileState[],
  externalId: string,
  on_premise_authorization_token?: string,
  on_premise_domain_ssl?: string,
): Promise<ProcessedFile[]> {
  const processedFiles: ProcessedFile[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    const base64Content = await getFileContentAsBase64(
      file,
      externalId,
      on_premise_authorization_token,
      on_premise_domain_ssl,
    );

    const fileType = getFileType(file);

    processedFiles.push({
      "gdb:Dateityp": fileType,
      "gdb:Datei": base64Content,
    });
  }

  return processedFiles;
}

// v2.0: Build array of facilities for Angefragte_Rehaeinrichtungen (§4.4)
function buildV2FacilitiesArray(
  form: ReturnType<
    typeof nullsToUndefined<GKVGeneralFormData & GKVMedicalFormData>
  >,
  schemaVersion: SchemaVersion,
) {
  const facilities = [];

  // Facility 1 always uses the existing field names (backward compat)
  facilities.push({
    "arh:Anschrift_Rehaeinrichtung": {
      "arh:Name": form.prefered_clinic_name,
      "arh:Strasse": form.prefered_clinic_street,
      "arh:Hausnummer": form.prefered_clinic_house_number,
      "arh:Postleitzahl": form.prefered_clinic_postal_code,
      "arh:Ort": form.prefered_clinic_city,
      "arh:Telefon": form.prefered_clinic_phone,
    },
    "arh:Aufnahme_moeglich": Number(
      form.prefered_clinic_admission_possible ?? "2",
    ),
    "arh:Fruehestmoegliche_Aufnahme": formatAhbXmlDate(
      form.prefered_clinic_contacted_start_date,
      schemaVersion,
    ),
  });

  // Additional facilities (2-5) use indexed field names
  for (const idx of ADDITIONAL_FACILITY_INDEXES) {
    const name = form[`prefered_clinic_${idx}_name` as keyof typeof form] as
      | string
      | undefined;
    if (!name) continue; // Skip empty facilities

    const street = form[
      `prefered_clinic_${idx}_street` as keyof typeof form
    ] as string | undefined;
    const houseNumber = form[
      `prefered_clinic_${idx}_house_number` as keyof typeof form
    ] as string | undefined;
    const postalCode = form[
      `prefered_clinic_${idx}_postal_code` as keyof typeof form
    ] as string | undefined;
    const city = form[`prefered_clinic_${idx}_city` as keyof typeof form] as
      | string
      | undefined;
    const phone = form[`prefered_clinic_${idx}_phone` as keyof typeof form] as
      | string
      | undefined;
    const admissionPossible = form[
      `prefered_clinic_${idx}_admission_possible` as keyof typeof form
    ] as string | undefined;
    const startDate = form[
      `prefered_clinic_${idx}_contacted_start_date` as keyof typeof form
    ] as number | undefined;

    facilities.push({
      "arh:Anschrift_Rehaeinrichtung": {
        "arh:Name": name,
        "arh:Strasse": street,
        "arh:Hausnummer": houseNumber,
        "arh:Postleitzahl": postalCode,
        "arh:Ort": city,
        "arh:Telefon": phone,
      },
      "arh:Aufnahme_moeglich": Number(admissionPossible ?? "2"),
      "arh:Fruehestmoegliche_Aufnahme": formatAhbXmlDate(startDate, schemaVersion),
    });
  }

  return facilities;
}

export async function mapFormToRehabRequest({
  excludePatientAddress,
  isProd,
  unsanitizedForm,
  files,
  externalId,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  documentNumber,
  schemaVersion,
}: {
  excludePatientAddress?: boolean;
  isProd: boolean;
  unsanitizedForm: GKVGeneralFormData & GKVMedicalFormData;
  files?: FileState[];
  externalId: string;
  on_premise_authorization_token?: string;
  on_premise_domain_ssl?: string;
  documentNumber: number;
  schemaVersion: SchemaVersion;
}) {
  let processedFiles: ProcessedFile[] = [];

  if (files?.length) {
    processedFiles = await processAndValidateFiles(
      files,
      externalId,
      on_premise_authorization_token,
      on_premise_domain_ssl,
    );
  }
  const form = nullsToUndefined(unsanitizedForm);
  const now = new Date();
  const zonedDate = utcToZonedTime(now, AHB_SUBMISSION_TIMEZONE);
  const erstellungsdatum = formatAhbXmlDate(now, schemaVersion);
  const erstellungszeit = format(zonedDate, "HHmmss");
  const dateiID = uuidv4();

  const rehabType = mapRecommendedRehabType(form.recommended_reha_type);
  const verkehrsmittel = mapTransportationCode(
    form.transport_type,
    form.transport_type_taxi_specification,
    form.transport_type_ambulance_specification,
  );

  const operations = Object.values(OPERATION_INDEXES)
    .map((index) => {
      const date = form[`operation_${index}_date`];
      const rawOps = form[`operation_${index}_ops`];
      const woundClosed = form[`operation_${index}_wound_closed`];

      const ops =
        Array.isArray(rawOps) && rawOps.length > 0
          ? rawOps.filter((code) => code.trim() !== "")
          : [];

      const hasDate = typeof date === "number";
      const hasWoundClosed = typeof woundClosed === "number";
      const hasOps = ops.length > 0;

      if (!hasDate && !hasWoundClosed && !hasOps) return undefined;

      return {
        "arh:Wundheilung": predicamentMappingYesNo(
          woundClosed,
          "arh:Wundheilung",
        ),
        "arh:OPS_Codes": { "arh:OPS": ops },
        "arh:OP_Datum": formatAhbXmlDate(date, schemaVersion)!,
      } as const;
    })
    .truthy();

  if (!erstellungsdatum) {
    throw new Error("Missing gdb:Erstellungsdatum");
  }

  const ns = AHB_NAMESPACES_BY_VERSION[schemaVersion];

  return {
    Nutzdaten: {
      "@xmlns": ns.xmlns,
      "@xmlns:gdb": ns.xmlnsGdb,
      "@xmlns:aar": ns.xmlnsAar,
      "@xmlns:arh": ns.xmlnsArh,
      "@xmlns:feh": ns.xmlnsFeh,
      "@xmlns:sto": ns.xmlnsSto,
      Header: {
        "gdb:Absender": {
          "gdb:Krankenhaus": {
            "gdb:Institutionskennzeichen": form.hospital_ik_number
              ? String(form.hospital_ik_number)
              : "",
          },
        },
        "gdb:Empfaenger": {
          "gdb:Krankenkasse": {
            "gdb:Institutionskennzeichen": form.insurance_ik_number
              ? String(form.insurance_ik_number)
              : "",
          },
        },
        "gdb:Erstellungsdatum": erstellungsdatum,
        "gdb:Erstellungszeit": erstellungszeit,
        "gdb:Datei_ID": dateiID,
        "gdb:Verfahrenskennung": isProd
          ? AHB_PROCEDURE_ID.PRODUCTION
          : AHB_PROCEDURE_ID.TEST,
        "gdb:Unterverfahren": AHB_SUBPROCEDURE,
        "gdb:Nachrichtentyp": AHB_MESSAGE_TYPE.REQUEST,
        "gdb:Logische_Version": schemaVersion,
        "gdb:Dateinummer": documentNumber,
      },
      Body: {
        Anschlussrehabilitation: {
          "aar:Antrag": {
            "aar:Versicherter": {
              "gdb:Nachname": form.patient_last_name!,
              "gdb:Vorname": form.patient_first_name!,
              ...(schemaVersion === SCHEMA_VERSION.V1_1_0 && form.insurance_number && excludePatientAddress
                ? {}
                : {
                    ...(schemaVersion === SCHEMA_VERSION.V2_0_0 &&
                    form.patient_postfach
                      ? { "gdb:Postfach": form.patient_postfach }
                      : {
                          "gdb:Strasse": form.patient_street,
                          ...(form.patient_housenumber && {
                            "gdb:Hausnummer": form.patient_housenumber,
                          }),
                        }),
                    ...(form.patient_zipcode && {
                      "gdb:Postleitzahl": form.patient_zipcode,
                    }),
                    ...(form.patient_city && {
                      "gdb:Ort": form.patient_city,
                    }),
                    "gdb:Anschriftenzusatz": undefined,
                    "gdb:Laenderkennzeichen": "D",
                  }),
              "gdb:Krankenversichertennummer": form.insurance_number!,
              "gdb:Geburtsdatum": formatAhbXmlDate(
                form.patient_birthdate,
                schemaVersion,
              )!,
              "gdb:Geschlecht": mapGender(form.gender)!,
              ...(form.patient_phone_number && {
                "gdb:Telefon": form.patient_phone_number,
              }),
              "gdb:KH_internes_Kennzeichen": form.case_id!,
            },
            "aar:Antrag": {
              "arh:Verarbeitungskennzeichen":
                AHB_PROCESSING_CODE.INITIAL_REQUEST,
              "arh:Angaben_des_Versicherten": {
                "arh:Antrag_Pflegebeduerftigkeit": predicamentMappingYesNo(
                  form.insured_details_application_insurance_benefits,
                  "arh:Antrag_Pflegebeduerftigkeit",
                ),
                "arh:Rentenantragstellung": {
                  "arh:Altersrente": predicamentMappingYesNo(
                    form.retirement_pension_applied,
                    "arh:Altersrente",
                  ),
                  "arh:Traeger_der_Altersrente":
                    form.retirement_pension_company,
                  "arh:Erwerbsminderungsrente": predicamentMappingYesNo(
                    form.retirement_pension_earning_capacity_applied,
                    "arh:Erwerbsminderungsrente",
                  ),
                  "arh:Traeger_der_Erwerbsminderungsrente":
                    form.retirement_earning_capacity_company,
                },
                "arh:Haeusliche_Situation": {
                  "arh:Stockwerk": form.living_lift_description || "u",
                  "arh:Aufzug": predicamentMappingYesNo(
                    form.living_lift,
                    "arh:Aufzug",
                  ),
                  ...(form.living_special?.trim() && {
                    "arh:Besonderheiten": form.living_special.trim(),
                  }),
                },
                "arh:Haeusliche_Versorgungssituation": {
                  "arh:Wohnsituation": mapLivingSituation(
                    form.living_situation,
                    form.living_situation_at_home,
                  ),
                  "arh:Versorgung_Sichergestellt": predicamentMappingYesNo(
                    form.domestic_situation_is_safe,
                    "arh:Versorgung_Sichergestellt",
                  ),
                },
                ...((form.general_practitioner_first_name ||
                  form.general_practitioner_last_name) && {
                  "arh:Hausarzt": {
                    "arh:Nachname": form.general_practitioner_last_name!,
                    "arh:Vorname": form.general_practitioner_first_name!,
                    ...(form.general_practitioner_street && {
                      "arh:Strasse": form.general_practitioner_street,
                    }),
                    ...(form.general_practitioner_housenumber && {
                      "arh:Hausnummer": form.general_practitioner_housenumber,
                    }),
                    ...(form.general_practitioner_zipcode && {
                      "arh:Postleitzahl": form.general_practitioner_zipcode,
                    }),
                    "arh:Ort": form.general_practitioner_city!,
                    ...(form.general_practitioner_phone_number && {
                      "arh:Telefon": form.general_practitioner_phone_number,
                    }),
                  },
                }),
                ...(form.insured_details_guardian_1_type && {
                  "arh:Betreuung_Vollmacht_Vertreter": [
                    {
                      "arh:Kennzeichen_Vertretung":
                        form.insured_details_guardian_1_type,
                      "arh:Nachname":
                        form.insured_details_guardian_1_last_name!,
                      "arh:Vorname":
                        form.insured_details_guardian_1_first_name!,
                      "arh:Strasse": form.insured_details_guardian_1_street,
                      ...(form.insured_details_guardian_1_house_number && {
                        "arh:Hausnummer":
                          form.insured_details_guardian_1_house_number,
                      }),
                      ...(form.insured_details_guardian_1_zipcode && {
                        "arh:Postleitzahl":
                          form.insured_details_guardian_1_zipcode,
                      }),
                      ...(form.insured_details_guardian_1_city && {
                        "arh:Ort": form.insured_details_guardian_1_city,
                      }),
                      ...(form.insured_details_guardian_1_phone && {
                        "arh:Telefon": form.insured_details_guardian_1_phone,
                      }),
                    },
                    ...(form.insured_details_guardian_2_type
                      ? [
                          {
                            "arh:Kennzeichen_Vertretung":
                              form.insured_details_guardian_2_type,
                            "arh:Nachname":
                              form.insured_details_guardian_2_last_name!,
                            "arh:Vorname":
                              form.insured_details_guardian_2_first_name!,
                            ...(form.insured_details_guardian_2_street && {
                              "arh:Strasse":
                                form.insured_details_guardian_2_street,
                            }),
                            ...(form.insured_details_guardian_2_house_number && {
                              "arh:Hausnummer":
                                form.insured_details_guardian_2_house_number,
                            }),
                            ...(form.insured_details_guardian_2_zipcode && {
                              "arh:Postleitzahl":
                                form.insured_details_guardian_2_zipcode,
                            }),
                            ...(form.insured_details_guardian_2_city && {
                            "arh:Ort": form.insured_details_guardian_2_city,
                            }),
                            ...(form.insured_details_guardian_2_phone && {
                              "arh:Telefon":
                                form.insured_details_guardian_2_phone,
                            }),
                          },
                        ]
                      : []),
                  ],
                }),
                ...(form.reason_for_reha_clinic && {
                  "arh:Anforderungen_Wuensche": form.reason_for_reha_clinic,
                }),
              },
              "arh:Willenserklaerung_Unterschrift":
                predicamentMappingYesUnknown(
                  form.declaration_of_intent_confirmed,
                ),
              "arh:Entbindung_Schweigepflicht": predicamentMappingYesNo(
                form.data_transfer_consent_given,
                "arh:Entbindung_Schweigepflicht",
              ),
              ...(schemaVersion !== SCHEMA_VERSION.V2_0_0 && {
                "arh:Einwilligung_Uebermittlung": predicamentMappingYesNo(
                  form.data_transfer_consent_given,
                  "arh:Einwilligung_Uebermittlung",
                ),
              }),
              "arh:Angaben_des_Krankenhauses": {
                // v2.0: Hospital name & address – flat per XSD sequence order
                ...(schemaVersion === SCHEMA_VERSION.V2_0_0 && {
                  "arh:Name_Krankenhaus": form.hospital_name,
                  "arh:Strasse": form.hospital_street,
                  ...(form.hospital_house_number && {
                    "arh:Hausnummer": form.hospital_house_number,
                  }),
                  "arh:Postleitzahl": form.hospital_postal_code,
                  "arh:Ort": form.hospital_city,
                }),
                "arh:Standortnummer": form.site_id_number!,
                "arh:Ansprechpartner": {
                  "arh:Nachname": form.social_worker_last_name!,
                  ...(form.social_worker_first_name && {
                    "arh:Vorname": form.social_worker_first_name,
                  }),
                  "arh:Telefon": form.social_worker_phone_number!,
                  ...(form.social_worker_fax_number && {
                    "arh:Fax": form.social_worker_fax_number,
                  }),
                },
                "arh:Kommunikation": {
                  "arh:Deutsche_Sprache": predicamentMappingYesNo(
                    form.communication_in_german_possible,
                    "arh:Deutsche_Sprache",
                  ),
                  ...(form.communication_other_language && {
                    "arh:Sprache": form.communication_other_language,
                  }),
                },
                "arh:Rehaeinrichtung":
                  form.prefered_clinic_contacted === PREDICAMENT_STATE_NO
                    ? {
                        "arh:Rehaeinrichtung_nicht_angefragt": 1 as const,
                      }
                    : schemaVersion === SCHEMA_VERSION.V2_0_0
                    ? // v2.0: new wrapper with repeatable facilities + Aufnahme_moeglich (§4.4)
                      {
                        "arh:Angefragte_Rehaeinrichtungen": {
                          "arh:Angefragte_Rehaeinrichtung":
                            buildV2FacilitiesArray(form, schemaVersion),
                        },
                      }
                    : // v1.1.0: single facility at Rehaeinrichtung level
                      {
                        "arh:Angefragte_Rehaeinrichtung": {
                          "arh:Anschrift_Rehaeinrichtung": {
                            "arh:Name": form.prefered_clinic_name,
                            "arh:Strasse": form.prefered_clinic_street,
                            "arh:Hausnummer": form.prefered_clinic_house_number,
                            "arh:Postleitzahl":
                              form.prefered_clinic_postal_code,
                            "arh:Ort": form.prefered_clinic_city,
                            "arh:Telefon": form.prefered_clinic_phone,
                          },
                        },
                        "arh:Fruehestmoegliche_Aufnahme": formatAhbXmlDate(
                          form.prefered_clinic_contacted_start_date,
                          schemaVersion,
                        ),
                      },
                ...(schemaVersion !== SCHEMA_VERSION.V2_0_0 && {
                  "arh:Krankenhausbehandlung": {
                    "arh:Beginn": formatAhbXmlDate(
                      form.admission_date,
                      schemaVersion,
                    )!,
                    "arh:Ende": formatAhbXmlDate(form.discharge_date, schemaVersion)!,
                    "arh:Version_ICD_OPS": "2023",
                  },
                }),
              },
              ...(schemaVersion === SCHEMA_VERSION.V2_0_0 && {
                "arh:Krankenhausbehandlung": {
                  "arh:Beginn": formatAhbXmlDate(form.admission_date, schemaVersion)!,
                  "arh:Ende": formatAhbXmlDate(form.discharge_date, schemaVersion)!,
                  "arh:Version_ICD_OPS": "2023",
                },
              }),
              "arh:Fruehrehabilitation": predicamentMappingYesNo(
                form.early_rehabilitation_measures,
                "arh:Fruehrehabilitation",
              ),
              "arh:Verlegung": {
                "arh:Kennzeichen_Verlegung": form.rehab_admission_type!,
                "arh:Datum_Verlegung":
                  form.rehab_admission_type ===
                  REHAB_ADMISSION.OUTSIDE_TWO_WEEKS
                    ? formatAhbXmlDate(
                        form.rehab_admission_outside_two_weeks_start_date,
                        schemaVersion,
                      )
                    : form.rehab_admission_type ===
                      REHAB_ADMISSION.WITHIN_TWO_WEEKS
                    ? formatAhbXmlDate(
                        form.rehab_admission_within_two_weeks_start_date,
                        schemaVersion,
                      )
                    : undefined,
                "arh:Begruendung":
                  form.rehab_admission_outside_two_weeks_explanation ??
                  undefined,
              },
              "arh:Funktionsdiagnosen": {
                "arh:Funktionsdiagnose": [0, 1, 2, 3, 4]
                  .map((i) => {
                    const icdCodeNumber = i + 1;
                    const rawCodeField = form[
                      `icd_code_${icdCodeNumber}_code` as "icd_code_1_code"
                    ] as string | { value?: string } | null | undefined;
                    const codeValue =
                      typeof rawCodeField === "object" && rawCodeField !== null
                        ? rawCodeField.value
                        : rawCodeField;
                    const code = codeValue?.replace(/\./g, "");

                    const diagnosis =
                      form[
                        `icd_code_${icdCodeNumber}_diagnosis` as "icd_code_1_diagnosis"
                      ];
                    const reason =
                      form[
                        `icd_code_${icdCodeNumber}_reason` as "icd_code_1_reason"
                      ];

                    const sideLocPrimary =
                      form[
                        `icd_code_${icdCodeNumber}_side_localization_primary` as "icd_code_1_side_localization_primary"
                      ];

                    return code || diagnosis || reason
                      ? {
                          "arh:Lfd_Nr": String(icdCodeNumber) as
                            | "1"
                            | "2"
                            | "3"
                            | "4"
                            | "5",
                          "arh:Diagnose": diagnosis!,
                          ...(schemaVersion === SCHEMA_VERSION.V2_0_0
                            ? {
                                "arh:ICD_10_GM_Primaerdiagnose": code!,
                                "arh:Seitenlokalisation_Primaerdiagnose":
                                  sideLocPrimary || undefined,
                              }
                            : { "arh:ICD_10_GM": code! }),
                          "arh:Ursache": reason!,
                        }
                      : undefined;
                  })
                  .truthy(),
              },
              "arh:Behandlungen": {
                "arh:Operation_durchgefuehrt": predicamentMappingYesNo(
                  form.surgery_performed,
                  "arh:Operation_durchgefuehrt",
                ),
                ...(!!operations.length && {
                  "arh:Operationen": {
                    "arh:Operation": operations,
                  },
                }),
                ...(form.other_treatments && {
                  "arh:Andere_Behandlung": form.other_treatments,
                }),
              },
              "arh:Komplikationen": {
                "arh:Komplikationen_aufgetreten": predicamentMappingYesNo(
                  form.complication_in_treatment_process,
                  "arh:Komplikationen_aufgetreten",
                ),

                ...(form.complication_in_treatment_process ===
                  PREDICAMENT_STATE_YES && {
                  "arh:Art_der_Komplikation": {
                    ...(form.complication_cause_maindiagnosis_description && {
                      "arh:Komplikation_mit_AR_Diagnose":
                        form.complication_cause_maindiagnosis_description,
                    }),
                    ...(form.complication_cause_cardiovascular_description && {
                      "arh:Kardiovaskulaere_Komplikation":
                        form.complication_cause_cardiovascular_description,
                    }),
                    ...(form.complication_cause_other_description && {
                      "arh:Sonstige_Komplikation":
                        form.complication_cause_other_description,
                    }),
                  },
                }),
                "arh:Keime": {
                  "arh:Keimbesiedelung": predicamentMappingYesNoUnknown(
                    form.infections_state,
                    "arh:Keimbesiedelung",
                  ),
                  ...(form.infections && {
                    "arh:Art_der_Keime": form.infections,
                  }),
                },
              },
              "arh:Alltagsrelevante_Beeintraechtigungen": (() => {
                const beeintraechtigungen = Object.entries(beeintraechtigungMap)
                  .map(([field, code]) => {
                    const key =
                      `singer_assessment_${field}` as keyof GKVMedicalFormData;
                    const value = form[key];
                    return {
                      "arh:Item": code as string,
                      "arh:Punkte":
                        typeof value === "number" ? value : undefined,
                    };
                  })
                  .filter((item) => typeof item["arh:Punkte"] === "number");

                const hasAnyNumbers = beeintraechtigungen.some(
                  (i) => typeof i["arh:Punkte"] === "number",
                );

                const gesamtpunkte = hasAnyNumbers
                  ? beeintraechtigungen.reduce(
                      (sum, i) =>
                        sum +
                        (typeof i["arh:Punkte"] === "number"
                          ? i["arh:Punkte"]
                          : 0),
                      0,
                    )
                  : undefined;

                return {
                  // Only include "arh:Beeintraechtigung" if there are any valid entries
                  "arh:Beeintraechtigung":
                    beeintraechtigungen.length > 0
                      ? beeintraechtigungen
                      : undefined,
                  "arh:Gesamtpunkte": gesamtpunkte,
                };
              })(),
              "arh:Rehabilitationsfaehigkeit": {
                "arh:Ausreichende_Therapiebelastbarkeit":
                  predicamentMappingYesNo(
                    form.limited_capacity,
                    "arh:Ausreichende_Therapiebelastbarkeit",
                  ),
                "arh:Belastbarkeit_nach_OP": (():
                  | "1"
                  | "2"
                  | "3"
                  | undefined => {
                  if (form.reha_capacity === MUSCOSKELETAL_RESILIENCE.FULLY)
                    return "1";
                  if (form.reha_capacity === MUSCOSKELETAL_RESILIENCE.PARTIALLY)
                    return "2";
                  if (form.reha_capacity === MUSCOSKELETAL_RESILIENCE.STABLE)
                    return "3";
                  return undefined;
                })(),
                "arh:Teilbelastbar_mit":
                  form.reha_capacity === MUSCOSKELETAL_RESILIENCE.PARTIALLY &&
                  form.reha_capacity_partial
                    ? safeParseInt(form.reha_capacity_partial.toString())
                    : undefined,
                "arh:Anpassung_Interimsprothese_erfolgt":
                  predicamentMappingYesNo(
                    form.interim_prosthesis_fitted,
                    "arh:Anpassung_Interimsprothese_erfolgt",
                  ),
                "arh:Besonderheiten": form.reha_capacity_other || undefined,
                "arh:Kriterien_FR_Index":
                  Array.isArray(form.reha_capacity_early_rehab_criteria) &&
                  form.reha_capacity_early_rehab_criteria.length > 0
                    ? {
                        "arh:Kriterium":
                          form.reha_capacity_early_rehab_criteria,
                      }
                    : undefined,
              },
              "arh:Rehabilitationsziel": form.rehabilitation_goals!,
              "arh:Zusammenfassende_Bewertung": {
                "arh:Schwerpunkt": {
                  "arh:Rehaschwerpunkt": form.rehab_focus
                    ? REHAB_FOCUS_VALUE_TO_CODE[form.rehab_focus]
                    : undefined,
                  "arh:Erlaeuterung":
                    form.rehab_focus == REHAB_FOCUS.OTHER
                      ? form.rehab_focus_other_description
                      : undefined,
                },
                "arh:Empfohlene_Rehabilitationsart": rehabType!,
                "arh:Begruendung_Rehabilitationsart":
                  form.recommended_reha_type_reason ?? undefined,
                "arh:Fall_Paragraph_16": predicamentMappingYesNo(
                  form.rehab_directive_state,
                  "arh:Fall_Paragraph_16",
                ),
                ...(predicamentMappingYesNo(
                  form.rehab_directive_state,
                  "arh:Fall_Paragraph_16",
                ) === "1" && form.rehab_directive_type
                  ? schemaVersion === SCHEMA_VERSION.V2_0_0
                    ? {
                        "arh:Begruendung": Array.isArray(
                          form.rehab_directive_type,
                        )
                          ? form.rehab_directive_type[0]
                          : form.rehab_directive_type,
                      }
                    : {
                        "arh:Begruendungen": {
                          "arh:Begruendung": Array.isArray(
                            form.rehab_directive_type,
                          )
                            ? form.rehab_directive_type
                            : [form.rehab_directive_type],
                        },
                      }
                  : {}),
              },
              "arh:Weitere_Informationen": {
                "arh:Besondere_Anforderungen": {
                  ...(form.special_requirements_required ===
                  PREDICAMENT_STATE_YES
                    ? {
                        "arh:Anforderungen": {
                          // v2.0: filter out removed keys 10, 11, 17 (§6.11)
                          "arh:Anforderung":
                            schemaVersion === SCHEMA_VERSION.V2_0_0
                              ? (form.special_requirements ?? []).filter(
                                  (req) =>
                                    req !==
                                      SPECIAL_REQUIREMENTS.OVERLENGTH_BED &&
                                    req !== SPECIAL_REQUIREMENTS.OBESITY &&
                                    req !== SPECIAL_REQUIREMENTS.WEIGHT_BED,
                                )
                              : form.special_requirements ?? [],

                          ...(form.special_requirements?.includes(
                            SPECIAL_REQUIREMENTS.DIALYSIS,
                          ) && {
                            "arh:Dialyseverfahren":
                              form.special_requirements_dialysis_description ??
                              undefined,
                          }),

                          ...(form.special_requirements?.includes(
                            SPECIAL_REQUIREMENTS.OTHER,
                          ) && {
                            "arh:Erlaeuterung":
                              form.special_requirements_other_description ??
                              undefined,
                          }),

                          ...(form.special_requirements?.includes(
                            SPECIAL_REQUIREMENTS.RELATIVES,
                          ) && {
                            "arh:Pflegegrad_Angehoeriger":
                              (form.special_requirements_relatives_description as
                                | "1"
                                | "2"
                                | "3"
                                | "4"
                                | "5") ?? undefined,
                          }),

                          ...(form.special_requirements?.includes(
                            SPECIAL_REQUIREMENTS.DECUBITUS,
                          ) && {
                            "arh:Grad_Dekubitus":
                              (form.special_requirements_decubitus_description as
                                | "1"
                                | "2"
                                | "3"
                                | "4") ?? undefined,
                          }),
                          // v2.0: arh:Last_bis removed (Schwerlastbett 17 no longer valid)
                          ...(schemaVersion !== SCHEMA_VERSION.V2_0_0 &&
                            form.special_requirements?.includes(
                              SPECIAL_REQUIREMENTS.WEIGHT_BED,
                            ) &&
                            form.special_requirements_weight_bed_description &&
                            !isNaN(
                              Number(
                                form.special_requirements_weight_bed_description,
                              ),
                            ) && {
                              "arh:Last_bis": Number(
                                form.special_requirements_weight_bed_description,
                              ),
                            }),
                        },
                      }
                    : {
                        "arh:Keine_besonderen_Anforderungen":
                          form.special_requirements_required ===
                          PREDICAMENT_STATE_NO
                            ? ("1" as const)
                            : undefined,
                      }),
                  ...(schemaVersion === SCHEMA_VERSION.V2_0_0 && {
                    "arh:Koerpergroesse": form.patient_body_height
                      ? Number(form.patient_body_height)
                      : undefined,
                    "arh:Koerpergewicht": form.patient_body_weight
                      ? Number(form.patient_body_weight)
                      : undefined,
                  }),
                },
                "arh:Anreise": {
                  "arh:Verkehrsmittel": verkehrsmittel!,
                  "arh:Begruendung_KTW":
                    form.transport_type_ambulance_description || undefined,
                  "arh:Begleitperson": predicamentMappingYesNo(
                    form.require_accompanying_person,
                    "arh:Begleitperson",
                  ),
                },
                "arh:Krankenhausarzt": {
                  "arh:Nachname": form.doctor_in_charge_in_hospital_last_name!,
                  "arh:Vorname": form.doctor_in_charge_in_hospital_first_name!,
                  "arh:Titel": undefined,
                  "arh:Telefon": form.doctor_in_charge_in_hospital_phone!,
                  "arh:Datum": formatAhbXmlDate(form.doctor_exam_date, schemaVersion),
                },
              },
            },
          },
        },
        // Add Dokumente section if files are uploaded
        ...(activateDTAAttachments &&
          !!processedFiles.length && {
            "gdb:Dokumente": {
              "gdb:Dokument": processedFiles,
            },
          }),
      },
    },
  };
}
